/**
 * One service request (tracker item 45, phase 4): what the customer asked for, who they are, and for a
 * NEW request Convert to draft job and Decline.
 *
 * Convert follows the backend rules: a request that already has its customer and property needs nothing
 * more; one without a customer needs an existing customer chosen here (CUSTOMER_REQUIRED), one with only a
 * typed address needs one of that customer's properties (PROPERTY_REQUIRED). Customers and properties are
 * never created from a request (the contact details are unverified). Both outcomes are final and the
 * customer is emailed, except for a website form request (tracker item 49), whose typed address is never
 * emailed.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { createAPIClient, IPropertiesApi, IRequestsApi } from '../../api';
import * as types from '../../api/types';
import { CustomerPicker } from '../../components/CustomerPicker';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';
import { getErrorMessage } from '../../utils/apiError';
import {
  REQUESTS_PATH, SERVICE_STATUS_LABELS, addressLine, isStaleRequestError, requestActionError, requestPlace, requestedWhen,
  requiredTextProblem, statusBadgeClass, whenLabel,
} from './requestsUi';

type Api = IRequestsApi & IPropertiesApi;

export function ServiceRequestPage() {
  const { id = '' } = useParams<{ id: string }>();
  const api = useMemo<Api>(() => createAPIClient(), []);
  const [request, setRequest] = useState<types.ServiceRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [customerId, setCustomerId] = useState('');
  const [properties, setProperties] = useState<types.Property[]>([]);
  const [propertiesError, setPropertiesError] = useState<string | null>(null);
  const [propertyId, setPropertyId] = useState('');
  const [mode, setMode] = useState<'convert' | 'decline'>('convert');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setRequest(await api.getServiceRequest(id));
    } catch (cause) {
      setRequest(null);
      setLoadError(getErrorMessage(cause, 'Could not load the service request'));
    } finally {
      setLoading(false);
    }
  }, [api, id]);

  useEffect(() => { void load(); }, [load]);

  const needsCustomer = !!request && request.status === 'NEW' && !request.customer;
  const needsProperty = !!request && request.status === 'NEW' && !request.property;
  // The customer whose properties are offered: the request's own, else the one chosen here
  const effectiveCustomerId = request?.customer?.id ?? customerId;

  useEffect(() => {
    setProperties([]);
    setPropertyId('');
    setPropertiesError(null);
    if (!needsProperty || !effectiveCustomerId) return undefined;
    let active = true;
    api.listProperties(effectiveCustomerId)
      .then((result) => { if (active) setProperties(result.data); })
      .catch((cause) => { if (active) setPropertiesError(getErrorMessage(cause, 'Could not load the customer\'s properties')); });
    return () => { active = false; };
  }, [api, needsProperty, effectiveCustomerId]);

  const convertProblem = !request ? null
    : needsCustomer && !customerId ? 'Choose the customer this request is for'
      : needsProperty && !propertyId ? 'Choose the property this request is for'
        : null;

  async function convert() {
    if (!request || convertProblem) { setError(convertProblem); return; }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const body: types.ConvertServiceRequestInput = {
        ...(needsCustomer ? { customerId } : {}),
        ...(needsProperty ? { propertyId } : {}),
      };
      const converted = await api.convertServiceRequest(request.id, body);
      setRequest(converted);
      setNotice(converted.source === 'WEB_FORM'
        ? 'A draft job was created from this request. Nobody is emailed for a website form request: contact the requester yourself.'
        : 'A draft job was created from this request. The customer is emailed.');
    } catch (cause) {
      setError(requestActionError(cause, 'Could not convert the request'));
      if (isStaleRequestError(cause)) await load();
    } finally {
      setBusy(false);
    }
  }

  async function decline() {
    if (!request) return;
    const problem = requiredTextProblem(reason, 'a reason for declining');
    if (problem) { setError(problem); return; }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const declined = await api.declineServiceRequest(request.id, reason.trim());
      setRequest(declined);
      setNotice(declined.source === 'WEB_FORM'
        ? 'The request was declined. Nobody is emailed for a website form request.'
        : 'The request was declined. The customer is emailed your reason.');
    } catch (cause) {
      setError(requestActionError(cause, 'Could not decline the request'));
      if (isStaleRequestError(cause)) await load();
    } finally {
      setBusy(false);
    }
  }

  if (loading && !request) return <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6"><p role="status">Loading service request…</p></main>;
  if (!request) {
    return (
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <Link to={REQUESTS_PATH} className="text-sm text-blue-700 hover:underline">← Requests</Link>
        <p role="alert" className="mt-4 rounded bg-red-50 p-3 text-red-800">{loadError ?? 'Service request not found'}</p>
      </main>
    );
  }

  const open = request.status === 'NEW';
  // The public web form (tracker item 49): typed by an anonymous visitor, never emailed back
  const webForm = request.source === 'WEB_FORM';
  return (
    <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <Link to={REQUESTS_PATH} className="text-sm text-blue-700 hover:underline">← Requests</Link>
      <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
        <h1 className="min-w-0 break-words text-2xl font-bold text-gray-900">{request.requestedItem?.name ?? 'Service request'}</h1>
        <span className={`rounded px-2 py-0.5 text-xs font-semibold ${statusBadgeClass(request.status)}`}>{SERVICE_STATUS_LABELS[request.status]}</span>
      </div>
      {notice && <Toast kind="success">{notice}</Toast>}

      <section aria-labelledby="request-details" className="mt-4 rounded-lg bg-white p-4 shadow sm:p-5">
        <h2 id="request-details" className="mb-3 text-lg font-semibold">Request</h2>
        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
          <div className="min-w-0"><dt className="text-gray-500">Customer</dt><dd className="break-words">
            {request.customer ? <Link to={`/customers/${request.customer.id}`} className="text-blue-700 hover:underline">{request.customer.name}</Link> : <span className="text-amber-700">Not matched to a customer yet</span>}
          </dd></div>
          <div className="min-w-0"><dt className="text-gray-500">Where</dt><dd className="break-words" data-testid="request-place">{requestPlace(request)}{request.property ? '' : request.serviceAddress ? ' (typed by the customer)' : ''}</dd></div>
          <div className="min-w-0"><dt className="text-gray-500">Service</dt><dd className="break-words">{request.requestedItem?.name ?? 'Not chosen'}</dd></div>
          <div className="min-w-0"><dt className="text-gray-500">Preferred times</dt><dd>{requestedWhen(request) || 'No preference'}</dd></div>
          <div className="min-w-0 sm:col-span-2"><dt className="text-gray-500">Notes</dt><dd className="whitespace-pre-wrap break-words">{request.notes || 'None'}</dd></div>
          <div className="min-w-0"><dt className="text-gray-500">Contact</dt><dd className="break-words">
            {request.contact.name}<br />{request.contact.email && <span className="break-all">{request.contact.email}<br /></span>}{request.contact.phone}
          </dd></div>
          <div className="min-w-0"><dt className="text-gray-500">Received</dt><dd>{whenLabel(request.createdAt)} via {webForm ? 'your website form' : 'the customer portal'}</dd></div>
        </dl>
        {webForm ? (
          <p className="mt-3 rounded bg-amber-50 p-2 text-xs text-amber-900" data-testid="web-form-note">
            Sent from your public website form without signing in: the name, contact details and address are unverified. Nobody is emailed when you
            convert or decline it, so contact the requester yourself.
          </p>
        ) : !request.customer && <p className="mt-3 rounded bg-amber-50 p-2 text-xs text-amber-900">The contact details were typed by the requester and are not verified against any customer.</p>}
      </section>

      {request.status === 'CONVERTED' && (
        <section className="mt-4 rounded-lg bg-green-50 p-4 text-green-900 ring-1 ring-green-200" data-testid="converted-note">
          Converted to a draft job{request.decidedAt ? ` on ${whenLabel(request.decidedAt)}` : ''}.{' '}
          {request.convertedJobId && <Link to={`/jobs/${request.convertedJobId}`} className="font-semibold underline">Open the job</Link>}
        </section>
      )}
      {request.status === 'DECLINED' && (
        <section className="mt-4 rounded-lg bg-red-50 p-4 text-red-900 ring-1 ring-red-200" data-testid="declined-note">
          Declined{request.decidedAt ? ` on ${whenLabel(request.decidedAt)}` : ''}. {webForm ? 'Reason (not sent)' : 'Reason given to the customer'}: {request.declineReason}
        </section>
      )}

      {open && (
        <section aria-labelledby="request-actions" className="mt-4 rounded-lg bg-white p-4 shadow sm:p-5">
          <h2 id="request-actions" className="mb-3 text-lg font-semibold">Answer this request</h2>
          <div role="tablist" aria-label="Answer" className="mb-4 flex gap-2">
            <button type="button" role="tab" aria-selected={mode === 'convert'} className={`min-h-[44px] rounded-md px-4 text-sm font-semibold ${mode === 'convert' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 ring-1 ring-gray-300'}`} onClick={() => { setMode('convert'); setError(null); }}>Convert to draft job</button>
            <button type="button" role="tab" aria-selected={mode === 'decline'} className={`min-h-[44px] rounded-md px-4 text-sm font-semibold ${mode === 'decline' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 ring-1 ring-gray-300'}`} onClick={() => { setMode('decline'); setError(null); }}>Decline</button>
          </div>

          {mode === 'convert' && (
            <form className="space-y-4" aria-label="Convert to draft job" onSubmit={(e) => { e.preventDefault(); void convert(); }}>
              <p className="text-sm text-gray-700">
                Creates a DRAFT job at the property with this request in its notes{request.requestedItem?.id ? ' and the requested service as a line' : ''}. Schedule and assign it from the job as usual.
                {webForm ? 'Nobody is emailed (website form request).' : 'The customer is emailed.'}
              </p>
              {needsCustomer && (
                <CustomerPicker id="convert-customer" label="Customer" value={customerId} disabled={busy} onChange={(value) => { setCustomerId(value); setError(null); }} />
              )}
              {needsProperty && (
                <div>
                  <label htmlFor="convert-property" className={labelClass}>Property</label>
                  <select id="convert-property" className={`${inputClass} min-h-[44px]`} value={propertyId} disabled={busy || !effectiveCustomerId} onChange={(e) => setPropertyId(e.target.value)}>
                    <option value="">{effectiveCustomerId ? 'Choose a property' : 'Choose the customer first'}</option>
                    {properties.map((p) => <option key={p.id} value={p.id}>{addressLine(p)}</option>)}
                  </select>
                  {request.serviceAddress && <p className="mt-1 text-xs text-gray-600">The customer typed: {request.serviceAddress}</p>}
                  {propertiesError && <p role="alert" className="mt-1 text-xs text-red-700">{propertiesError}</p>}
                  {effectiveCustomerId && !propertiesError && properties.length === 0 && (
                    <p className="mt-1 text-xs text-amber-800">This customer has no properties yet. Add the property to the customer first, then convert.</p>
                  )}
                </div>
              )}
              {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
              <button type="submit" className={primaryButtonClass} disabled={busy || !!convertProblem}>{busy ? 'Converting…' : 'Convert to draft job'}</button>
            </form>
          )}

          {mode === 'decline' && (
            <form className="space-y-4" aria-label="Decline request" onSubmit={(e) => { e.preventDefault(); void decline(); }}>
              <div>
                <label htmlFor="decline-reason" className={labelClass}>{webForm ? 'Reason (required; kept on the request, not sent)' : 'Reason (required; the customer sees it)'}</label>
                <textarea id="decline-reason" className={inputClass} rows={3} maxLength={500} value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)} />
              </div>
              {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
              <button type="submit" className={secondaryButtonClass} disabled={busy}>{busy ? 'Declining…' : 'Decline request'}</button>
            </form>
          )}
        </section>
      )}
    </main>
  );
}
